import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { validateConfirmPassword, validateEmail, validatePassword, validateUsername } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import Eye from '../../assets/icons/eye.svg?react';
import EyeSlash from '../../assets/icons/eyeSlash.svg?react';
import { useStation } from "../../contexts/StationContext";
import { Checkbox } from "../../components/ui/Checkbox";

export const SignUp: React.FC = () => {
    const { station } = useStation();
    const { signUpUser, error } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [form, setForm] = useState<{
        name: { value: string, error: string };
        email: { value: string, error: string };
        password: { value: string, error: string };
        confirmPassword: { value: string, error: string };
        terms: { value: boolean, error: string };
    }>({ name: { value: '', error: '' }, email: { value: '', error: '' }, password: { value: '', error: '' }, confirmPassword: { value: '', error: '' }, terms: { value: false, error: '' } });

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setForm(prev => ({ ...prev, [e.target.name as keyof typeof prev]: { ...prev[e.target.name as keyof typeof prev], value: e.target.value, error: '' } }));
    }

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        // Validate all fields at once
        const nameError = validateUsername(form.name.value);
        const emailError = validateEmail(form.email.value);
        const passwordError = validatePassword(form.password.value);
        const confirmPasswordError = validateConfirmPassword(form.confirmPassword.value, form.password.value);
        const termsError = !form.terms.value ? 'Please agree to the terms and conditions' : '';
        
        // Update form with all errors
        setForm(prev => ({
            ...prev,
            name: { ...prev.name, error: nameError },
            email: { ...prev.email, error: emailError },
            password: { ...prev.password, error: passwordError },
            confirmPassword: { ...prev.confirmPassword, error: confirmPasswordError },
            terms: { ...prev.terms, error: termsError }
        }));
        
        // Only proceed if all validations pass
        if (!nameError && !emailError && !passwordError && !confirmPasswordError && !termsError) {
            signUpUser({ 
                name: form.name.value.trim(), 
                email: form.email.value, 
                password: form.password.value.trim(), 
                stationDetail: { stationId: station.id, stationName: station.name } 
            });
        }
    };

    useEffect(() => {
        if (error?.includes('Email already exists')) {
            setForm(prev => ({ ...prev, email: { ...prev.email, error: 'Email already exists' } }));
        }
    }, [error]);

    return (
        <section className="flex flex-col gap-4 w-full">
            <h2 className="sm:text-2xl text-xl font-medium text-center text-white">Sign Up and Stay Tuned with RedFM</h2>
            <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
                <Input
                    variant="secondary"
                    type="text"
                    name="name"
                    label="Name"
                    placeholder="Enter your name"
                    minLength={3}
                    maxLength={20}
                    error={form.name.error}
                    value={form.name.value}
                    onChange={handleChange}
                    className={`w-full`}
                />
                <Input
                    variant="secondary"
                    type="email"
                    name="email"
                    label="Email"
                    placeholder="Enter your email"
                    maxLength={200}
                    error={form.email.error}
                    value={form.email.value}
                    onChange={handleChange}
                    className={`w-full`}
                />
                <Input
                    variant="secondary"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    label="Password"
                    placeholder="Enter your password"
                    hint={"Password should be the combination of minimum 8 characters with 1 lowercase, 1 uppercase, 1 number & 1 special character(!@#$%&*`~)"}
                    maxLength={20}
                    error={form.password.error}
                    value={form.password.value}
                    onChange={handleChange}
                    iconPosition="trailing"
                    icon={showPassword ? <Eye /> : <EyeSlash />}
                    onIconClick={() => setShowPassword(!showPassword)}
                    className={`w-full`}
                />
                <Input
                    variant="secondary"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="Enter your password again"
                    maxLength={20}
                    error={form.confirmPassword.error}
                    value={form.confirmPassword.value}
                    onChange={handleChange}
                    iconPosition="trailing"
                    icon={showConfirmPassword ? <Eye /> : <EyeSlash />}
                    onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`w-full`}
                />
                <div className="ml-[2px]">
                    <Checkbox
                        description="By clicking on Sign Up, you agree to RedFM's Terms of use and Privacy Policy"
                        variant="secondary"
                        isChecked={form.terms.value}
                        onCheck={() => setForm(prev => ({ ...prev, terms: { ...prev.terms, value: !prev.terms.value } }))}
                    />
                    {form.terms.error && <p className="text-red-500 text-sm">{form.terms.error}</p>}
                </div>
                <Button
                    type="submit"
                    variant="primary"
                    className="w-full mt-2"
                >
                    Sign Up
                </Button>
            </form>
        </section>
    );
};